/**
 * InvestorPage — the "Investor Data" section. 12 KPIs in three blocks:
 *   • Scale     (4 totals — windowed by date pill)
 *   • Money     (3 wallet balances + 1 derived net float — current snapshot)
 *   • Virality  (4 network-effect metrics — windowed by date pill)
 *
 * Live values come from GET /api/v1/investor via useInvestorData().
 *
 * Per CEO directive 2026-04-28: NO mock fallback. On backend failure, the
 * page renders an explicit error banner — no investor ever sees a fabricated
 * number.
 *
 * Wallet semantics: the "Money on platform" block is a CURRENT SNAPSHOT and
 * does not react to the date pill — current liability has no time dimension.
 * Tile sublabels say so explicitly.
 *
 * Spec: docs/METRICS.md §1, §2, §5
 */
import React from "react";
import { tokensV2 as T } from "./tokens.v2";
import { PageGuide } from "./PageGuide.v2";
import { Section } from "./Section.v2";
import { Tile } from "./Tile.v2";
import { useInvestorData } from "./useInvestorData.v2";
import { rangeWordFor, capRangeWord } from "./rangeWord.v2";
import { CreatorConcentrationCard, UnitEconomicsBlock } from "./InsightsCards.v2";
import { LoadingShell, EmptyError } from "./PageState.v2";

// ───────────────────────────────────────────────
//  Formatters
// ───────────────────────────────────────────────
function formatINR(n) {
  if (n === null || n === undefined) return "—";
  const num = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(num)) return "—";
  if (num >= 1e7) return "Rs " + (num / 1e7).toFixed(2) + "Cr";
  if (num >= 1e5) return "Rs " + (num / 1e5).toFixed(2) + "L";
  if (num >= 1e3) return "Rs " + (num / 1e3).toFixed(1) + "K";
  return "Rs " + Math.round(num);
}
function formatCount(n) {
  if (n === null || n === undefined) return "—";
  const num = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("en-IN");
}
function formatRatio(n) {
  if (n === null || n === undefined) return "—";
  const num = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(num)) return "—";
  return (num * 100).toFixed(1) + "%";
}

// ───────────────────────────────────────────────
//  Tile-builder helpers — keep tile shape identical
//  across all 12 metrics; only the values change.
// ───────────────────────────────────────────────
function tileForScale(d, key, opts) {
  const m = d.scale[key] || {};
  return {
    metricId: opts.metricId,
    label:    opts.label,
    icon:     opts.icon,
    variant:  opts.variant,
    value:    formatINR(m.value),
    subValue: opts.sub,
  };
}
function tileForMoney(d, key, opts) {
  const m = d.money[key] || {};
  return {
    metricId: opts.metricId,
    label:    opts.label,
    icon:     opts.icon,
    variant:  opts.variant,
    value:    formatINR(m.value),
    subValue: opts.sub,
  };
}

// ───────────────────────────────────────────────
//  Header — title + "as of" timestamp + range
// ───────────────────────────────────────────────
function Header({ data, loading, onRefresh, word }) {
  const refreshedAt = data && data.refreshedAt ? new Date(data.refreshedAt) : null;
  return (
    <div style={{
      display: "flex", alignItems: "baseline", justifyContent: "space-between",
      gap: 12, flexWrap: "wrap", marginBottom: 16, padding: "0 4px",
    }}>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.text.primary, letterSpacing: "-0.01em" }}>Investor Data · {word}</div>
        <div style={{ fontSize: 12, color: T.text.muted, marginTop: 4 }}>
          {loading ? "Loading KPIs from Postgres…" :
           refreshedAt ? `Refreshed ${refreshedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} IST` :
           "—"}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          style={{
            background: T.surface.card, border: `1px solid ${T.border.subtle}`,
            borderRadius: 999, padding: "6px 14px",
            fontSize: 12, fontWeight: 700, color: T.text.secondary,
            cursor: loading ? "not-allowed" : "pointer", fontFamily: T.font.sans,
            opacity: loading ? 0.6 : 1,
          }}
        >Refresh</button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────
//  Page
// ───────────────────────────────────────────────
export function InvestorPage({ range, rangeId, onOpenMetric }) {
  const [refreshKey, setRefreshKey] = React.useState(0);
  const { data, loading, error, reload } = useInvestorData(range, rangeId, refreshKey);
  const onRefresh = () => setRefreshKey((k) => k + 1);
  const word = rangeWordFor(rangeId);
  const Word = capRangeWord(rangeId);
  const isTillDate = data?.range?.isTillDate ?? (rangeId === "till-date");
  const scaleSubWord = isTillDate ? "lifetime" : word;
  const scaleSubCap  = isTillDate ? "Lifetime" : Word;

  // Loading state — pure skeleton, no other UI competes during a fetch.
  if (loading) {
    return <LoadingShell tiles={4} />;
  }

  // Error state — backend failed. Friendly empty state (no raw HTTP leaked).
  if (!data) {
    return <EmptyError title="Couldn't load Investor data" error={error} onRetry={reload} />;
  }

  // ─ Section 1: Scale (windowed) ─
  const scaleTiles = [
    tileForScale(data, "brandDealsLifetime",     { metricId: "brand-deals-lifetime",     label: "Brand deals closed",   icon: "B", variant: "muted-violet",  sub: `${scaleSubCap} · completed slots` }),
    tileForScale(data, "gmvLifetime",            { metricId: "gmv-lifetime",             label: "Total inflow (GMV)",  icon: "G", variant: "muted-orange",  sub: `Call revenue + brand inflow · ${scaleSubWord}` }),
    tileForScale(data, "creatorPayoutsLifetime", { metricId: "creator-payouts-lifetime", label: "Paid to creators",     icon: "P", variant: "muted-lime",    sub: `Completed withdrawals · ${scaleSubWord}` }),
    tileForScale(data, "refundsLifetime",        { metricId: "refunds-lifetime",         label: "Refunds issued",       icon: "R", variant: "muted-magenta", sub: `Fan-initiated only · ${scaleSubWord}` }),
  ];
  // Override count for brand-deals (shown as integer, not currency)
  scaleTiles[0].value = formatCount(data.scale?.brandDealsLifetime?.value);

  // ─ Section 2: Money on platform (always live snapshot) ─
  const moneyTiles = [
    tileForMoney(data, "fanWalletBalance",     { metricId: "wallet-fans",     label: "Fan wallets",     icon: "F", variant: "muted-orange",  sub: "Deposit + referral + campaign · current" }),
    tileForMoney(data, "creatorWalletBalance", { metricId: "wallet-creators", label: "Creator wallets", icon: "C", variant: "muted-lime",    sub: "All 3 pots · earned, not withdrawn · current" }),
    tileForMoney(data, "brandWalletBalance",   { metricId: "wallet-brands",   label: "Brand wallets",   icon: "B", variant: "muted-magenta", sub: "Remaining campaign budgets · current" }),
    tileForMoney(data, "netFloat",             { metricId: "net-float",       label: "Net float held",  icon: "N", variant: "muted-violet",  sub: "Total platform liability · current" }),
  ];

  // ─ Section 3: Virality (windowed) ─
  // Defensive default so missing-section response doesn't blow up the page;
  // the optional-chaining on each leaf below will then render "—" for each
  // metric instead of throwing.
  const v = data.virality || {};
  const viralityTiles = [
    {
      metricId: "creators-brought-fans",
      label: "Fans brought by creators",
      icon: "C",
      variant: "muted-orange",
      value: formatCount(v.creatorsBroughtFans?.invitees),
      subValue: `${formatCount(v.creatorsBroughtFans?.inviters)} creators · ${(v.creatorsBroughtFans?.avgPerInviter || 0).toFixed(1)} avg fans · ${scaleSubWord}`,
    },
    {
      metricId: "fans-brought-fans",
      label: "Fans brought by fans",
      icon: "F",
      variant: "muted-violet",
      value: formatCount(v.fansBroughtFans?.invitees),
      subValue: `${formatCount(v.fansBroughtFans?.inviters)} fans · ${(v.fansBroughtFans?.avgPerInviter || 0).toFixed(1)} avg invitees · ${scaleSubWord}`,
    },
    {
      metricId: "creators-brought-creators",
      label: "Creators brought creators",
      icon: "S",
      variant: "muted-lime",
      value: formatCount(v.creatorsBroughtCreators?.invitees),
      subValue: `${formatCount(v.creatorsBroughtCreators?.inviters)} creators · supply flywheel · ${scaleSubWord}`,
    },
    {
      metricId: "viral-coefficient",
      label: "K-factor (viral coeff.)",
      icon: "K",
      variant: "muted-magenta",
      value: formatRatio(v.viralCoefficient?.kFactor),
      subValue: `${formatCount(v.viralCoefficient?.signupsViaReferral)} of ${formatCount(v.viralCoefficient?.signupsTotal)} signups · ${scaleSubWord}`,
    },
  ];

  const handleClick = (tile) => () => {
    if (onOpenMetric && tile.metricId) {
      onOpenMetric(tile.metricId, {
        value: tile.value, subValue: tile.subValue, label: tile.label,
      });
    }
  };

  return (
    <>
      <Header data={data} loading={loading} onRefresh={onRefresh} word={word} />

      <Section
        title={`Scale · ${scaleSubWord}`}
        accent="#7C4DFF"
        count={scaleTiles.length}
        summary="Every paid call, brand deal, refund, and creator payout in the selected window."
      >
        {scaleTiles.map((t) => (
          <Tile key={t.metricId} {...t} onClick={handleClick(t)} />
        ))}
      </Section>

      {/* Creator concentration risk tile — top 5/10/20 share of total earnings */}
      <div style={{ marginBottom: 14 }}>
        <CreatorConcentrationCard range={range} rangeId={rangeId} />
      </div>

      {/* Unit economics: LTV, gross margin, referred-vs-organic */}
      <UnitEconomicsBlock />

      <Section
        title="Money on platform · live"
        accent="#FC8019"
        count={moneyTiles.length}
        summary="Total cash Fancall is sitting on right now — every rupee owed to a user across all 3 wallet pots. Snapshot does not react to the date pill — current liability has no time dimension."
      >
        {moneyTiles.map((t) => (
          <Tile key={t.metricId} {...t} onClick={handleClick(t)} />
        ))}
      </Section>

      <Section
        title={`Virality · ${scaleSubWord}`}
        accent="#4CAF50"
        count={viralityTiles.length}
        summary="Who brings whom. Fake-campaign-account rows (role_id = -7) are filtered out at the query layer."
      >
        {viralityTiles.map((t) => (
          <Tile key={t.metricId} {...t} onClick={handleClick(t)} />
        ))}
      </Section>

      {/* Firebase Auth identity count — complements PG signup totals.
          Only renders when Firebase Admin SDK is configured + warmed.
          Hides cleanly otherwise so the page stays clean. */}
      {data.firebaseAuth && Number.isFinite(data.firebaseAuth.totalUsers) && (
        <Section
          title="Identity · live"
          accent="#FFC107"
          count={1}
          summary="Total Firebase Auth identities — includes signed-in users who didn't complete in-app signup. Always ≥ the PG users count above."
        >
          <Tile
            label="Firebase Auth users"
            icon="ID"
            variant="muted-yellow"
            value={formatCount(data.firebaseAuth.totalUsers)}
            subValue="all Firebase Auth identities · live snapshot"
          />
        </Section>
      )}
      <PageGuide groups={INVESTOR_GUIDE} />
    </>
  );
}

// Definitions for every Investor-page card. Money excludes test accounts + NaN.
// "Money on platform" is a live snapshot (no date filter).
const INVESTOR_GUIDE = [
  {
    section: "Scale (window)",
    rows: [
      { card: "Brand deals closed", means: "Completed brand-campaign creator slots.", rule: "campaign_selected_creators status='completed', windowed on completion date." },
      { card: "Total inflow (GMV)", means: "Call revenue (fan calls/rooms) plus brand wallet deposits.", rule: "transaction_history (fan call/room) + brand_transaction_history (wallet deposits). NaN-guarded." },
      { card: "Paid to creators", means: "Completed creator + brand payouts.", rule: "withdraw_requests + brand_withdraw_requests, status='completed'." },
      { card: "Refunds issued", means: "Approved fan refunds.", rule: "refund_requests, refund_status='approved'." },
    ],
  },
  {
    section: "Money on platform (live)",
    rows: [
      { card: "Fan / Creator / Brand wallets", means: "Money held in each group's wallets right now (all three pots).", rule: "wallets (balance + referral + campaign) + brand_wallet. No date filter." },
      { card: "Net float held", means: "Total platform liability.", rule: "fan + creator + brand wallet balances." },
    ],
  },
  {
    section: "Virality (window)",
    rows: [
      { card: "Fans by creators / by fans / Creators by creators", means: "Referral counts by flow.", rule: "user_referral by role pair; excludes the synthetic account (-7)." },
      { card: "K-factor (viral coeff.)", means: "Share of signups that came via a referral.", rule: "referred signups ÷ total signups in window." },
    ],
  },
  {
    section: "Risk & economics",
    rows: [
      { card: "Creator concentration", means: "What share of payouts goes to the top 5 / 10 / 20 creators (dependency risk).", rule: "earnings ranked per creator, in window." },
      { card: "Fan LTV", means: "Distribution of lifetime spend per paying fan (avg / median / p90).", rule: "transaction_history per fan, lifetime." },
      { card: "Gross margin (90-day proxy)", means: "(GMV − creator payouts − refunds) ÷ GMV over the last 90 days.", rule: "transaction_history − earnings − refunds, rolling 90d." },
      { card: "Referred vs organic LTV", means: "Lifetime spend: referred fans vs organic fans.", rule: "transaction_history split by referral membership." },
      { card: "Firebase Auth users", means: "Total Firebase identities (≥ Postgres signups, since some never finish in-app signup).", rule: "Firebase Admin SDK (live, cached ~1h)." },
    ],
  },
];
